import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { UseFormSetError } from 'react-hook-form';
import { AxiosError } from 'axios';

import { client } from '@/utils/axios/axiosClient';
import { apiUri } from '@/constants/Constants';
import { UserPermissions } from '@/constants/permissions.constant';
import { checkIfPmi, showSmbInvite } from '@/utils/commonUtils';
import { hasUserPermission, permissionIsNotSet } from '@/utils/permissions.util';
import { useCurrentUser } from '@/stores/useCurrentUser';
import { useSplitFeatureFlag } from '@/hooks/useSplitFeatureFlag';
import { SplitFeatureFlag } from '@/utils/split/splitConfig';
import { useAnalyticsContext } from '@/utils/analytics/AnalyticsContext';
import { Renter, RenterInviteType } from '@/utils/types/renter.interface';
import type { User } from '@/utils/types/User';
import { useHasCustomScreeningStatusLabels, useInviteTracking } from './hooks';
import { InviteSegmentEvents } from './constants';
import { DdClient } from '@/features/DealsDashboard/service/DealsDashboardAPI';
import {
  buildRenterInvitePayload,
  buildLegacyRequestPayload,
  normalizeRenterInviteValues,
  applyInviteServiceFieldErrors,
  type SubmitErrorData,
} from './utils';

type SetErrorFn = UseFormSetError<Renter>;

const applyApplicantEmailFieldErrors = (
  errorsData: Record<string, string>,
  setError: SetErrorFn,
) => {
  Object.keys(errorsData).forEach((index) => {
    const msg = errorsData[index];
    if (msg !== '') {
      setError(`applicantEmails.${parseInt(index)}.value`, { message: msg });
    }
  });
};

const applyFaultMessagesToForm = (errorMsgs: Record<string, unknown>, setError: SetErrorFn) => {
  Object.keys(errorMsgs).forEach((key) => {
    const errors = errorMsgs[key];
    if (key === 'applicantEmails' && errors && typeof errors === 'object') {
      applyApplicantEmailFieldErrors(errors as Record<string, string>, setError);
    } else if (typeof errors === 'string' && /applicantEmails/.exec(errors)) {
      const match = /\[(\d+)\]/.exec(errors);
      const index = match?.[1];
      if (index) {
        setError(`applicantEmails.${parseInt(index)}.value`, { message: 'Invalid email.' });
      }
    }
  });
};

const getUserType = (user: User) => {
  if (checkIfPmi(user)) return 'PMI';
  return user.LLType;
};

type UseRenterInviteControllerOptions = {
  onSuccess?: (values: Renter) => void;
};

export const useRenterInviteController = (options?: UseRenterInviteControllerOptions) => {
  const [errorMsg, setErrorMsg] = useState<string>('');
  const hasTriggeredOnLoadEventsRef = useRef(false);
  const { track } = useAnalyticsContext();
  const { trackInvite } = useInviteTracking();

  const { evaluate: evaluateInviteServiceFlag } = useSplitFeatureFlag(
    SplitFeatureFlag.REN_11352_USE_INVITE_SERVICE_FOR_LL_INVITE,
  );

  const {
    treatment: killSwitchNewCoveragesOnRenterInvite,
    isReady: killSwitchNewCoveragesOnRenterInviteIsReady,
  } = useSplitFeatureFlag(SplitFeatureFlag.MONARCH_319_KILL_SWITCH_NEW_COVERAGES_ON_RENTER_INVITE);

  const { treatment: newCoveragesABTestEnabled, isReady: newCoveragesABTestEnabledIsReady } =
    useSplitFeatureFlag(SplitFeatureFlag.MONARCH_319_NEW_COVERAGES_ON_LANDLORD_INVITE);

  const { user } = useCurrentUser();

  const isSmbRenterInvites = useMemo(() => (user ? showSmbInvite(user) : false), [user]);
  const hasCustomScreeningStatusLabels = useHasCustomScreeningStatusLabels(isSmbRenterInvites);

  const useNewForm = useMemo(() => {
    if (killSwitchNewCoveragesOnRenterInvite === 'off') return false;
    if (!user || !isSmbRenterInvites) return false;
    return newCoveragesABTestEnabled === 'on';
  }, [killSwitchNewCoveragesOnRenterInvite, newCoveragesABTestEnabled, user, isSmbRenterInvites]);

  useEffect(() => {
    const shouldTriggerFirstLoadEvents =
      newCoveragesABTestEnabledIsReady &&
      killSwitchNewCoveragesOnRenterInviteIsReady &&
      killSwitchNewCoveragesOnRenterInvite === 'on' &&
      isSmbRenterInvites &&
      !hasTriggeredOnLoadEventsRef.current &&
      !!user;

    if (!shouldTriggerFirstLoadEvents) return;

    hasTriggeredOnLoadEventsRef.current = true;

    const renterInviteType = useNewForm
      ? RenterInviteType.SMBInviteNewLimitedSdr
      : RenterInviteType.SMBInviteOldCustomCoverage;

    track('Renter Invite form Visited', {
      newAbTests: {
        variant: renterInviteType,
        experiment: SplitFeatureFlag.MONARCH_319_NEW_COVERAGES_ON_LANDLORD_INVITE,
      },
      landlordType: getUserType(user),
      referrer: window.location.pathname,
    });
  }, [
    killSwitchNewCoveragesOnRenterInviteIsReady,
    useNewForm,
    track,
    user,
    newCoveragesABTestEnabledIsReady,
    killSwitchNewCoveragesOnRenterInvite,
    isSmbRenterInvites,
  ]);

  const customCoverageDisabled = useMemo(() => {
    if (!user) return false;

    const isPermissionNotSet = permissionIsNotSet(
      UserPermissions.Deal.CanCustomizeCoverages,
      user.userPermissions || [],
    );
    if (isPermissionNotSet) return false;

    return !hasUserPermission(
      UserPermissions.Deal.CanCustomizeCoverages,
      user.userPermissions || [],
    );
  }, [user]);

  const trackServerValidationErrors = useCallback(
    (errors: Array<{ formField: string; message: string }>) => {
      if (errors.length === 0) return;
      trackInvite(InviteSegmentEvents.ValidationErrorsShown, {
        validation_source: 'server',
        error_category: 'server_validation',
        errors: errors.map(({ formField, message }) => ({
          field_name: formField,
          error_code: 'server_validation',
          error_message_shown: message,
        })),
      });
    },
    [trackInvite],
  );

  const trackFaultValidationErrors = useCallback(
    (faultMessages: Record<string, unknown>) => {
      const errors: Array<{ formField: string; message: string }> = [];
      Object.entries(faultMessages).forEach(([fieldName, msg]) => {
        if (!msg) return;
        if (fieldName === 'applicantEmails' && typeof msg === 'object') {
          Object.entries(msg as Record<string, string>).forEach(([index, emailMsg]) => {
            if (emailMsg)
              errors.push({ formField: `applicantEmails.${index}.value`, message: emailMsg });
          });
          return;
        }
        if (typeof msg === 'string') errors.push({ formField: fieldName, message: msg });
      });
      trackServerValidationErrors(errors);
    },
    [trackServerValidationErrors],
  );

  const onSubmit = useCallback(
    async (values: Renter, setError: UseFormSetError<Renter>, submitAttemptNumber?: number) => {
      setErrorMsg('');

      const state = values.address.state?.trim().toUpperCase() ?? '';
      const { isFeatureFlagEnabled } = await evaluateInviteServiceFlag(state);
      const useInviteService = isFeatureFlagEnabled;
      const normalizedValues = normalizeRenterInviteValues(
        values,
        useNewForm,
        user,
        isSmbRenterInvites,
      );

      const requestPayload = useInviteService
        ? buildRenterInvitePayload(normalizedValues)
        : buildLegacyRequestPayload(normalizedValues);

      const requestClient = useInviteService ? DdClient : client;
      const requestUri = useInviteService ? apiUri.inviteViaDealsService : apiUri.inviteRenter;
      const requestOptions = useInviteService ? {} : { withCredentials: true };

      const submitProps = {
        property_id: normalizedValues.address?.property_id,
        screening_label_id: normalizedValues.approvalStatus,
        has_custom_screening_status_labels: hasCustomScreeningStatusLabels(
          normalizedValues.address?.property_id,
        ),
        submit_attempt_number: submitAttemptNumber ?? 1,
      };

      const handleSuccess = () => {
        trackInvite(InviteSegmentEvents.Submit, { ...submitProps, outcome: 'success' });
        options?.onSuccess?.(values);
      };

      const handleInviteServiceError = (data: SubmitErrorData | undefined) => {
        if (data?.field_errors?.length) {
          const applied = applyInviteServiceFieldErrors(data.field_errors, setError);
          trackServerValidationErrors(applied);
          setErrorMsg('Please correct the errors above.');
          return;
        }
        setErrorMsg(data?.message || 'An error occurred while sending the invite.');
      };

      const handleLegacyError = (data: SubmitErrorData | undefined, fallbackMessage: string) => {
        const faultMessages = data?.fault?.messages;
        if (faultMessages && typeof faultMessages === 'object') {
          applyFaultMessagesToForm(faultMessages as Record<string, unknown>, setError);
          trackFaultValidationErrors(faultMessages as Record<string, unknown>);
          setErrorMsg('Please correct the errors above.');
          return;
        }
        setErrorMsg(fallbackMessage);
      };

      const handleFailure = (reason: AxiosError<SubmitErrorData>) => {
        trackInvite(InviteSegmentEvents.Submit, { ...submitProps, outcome: 'failure' });
        const data = reason.response?.data;
        if (useInviteService) {
          handleInviteServiceError(data);
          return;
        }
        handleLegacyError(data, reason.message);
      };

      try {
        await requestClient
          .post(requestUri, requestPayload, requestOptions)
          .then(handleSuccess)
          .catch(handleFailure);
      } catch (_e) {
        trackInvite(InviteSegmentEvents.Submit, { ...submitProps, outcome: 'failure' });
        setErrorMsg('Error while inviting renter');
      }
    },
    [
      useNewForm,
      evaluateInviteServiceFlag,
      user,
      isSmbRenterInvites,
      options,
      hasCustomScreeningStatusLabels,
      trackInvite,
      trackFaultValidationErrors,
      trackServerValidationErrors,
    ],
  );

  return {
    user,
    errorMsg,
    isSmbRenterInvites,
    useNewForm,
    customCoverageDisabled,
    onSubmit,
  };
};
